'use client';

import customFetch from '@/lib/utils/fetch';
import {
  buildTtsCacheKey,
  getCachedAudio,
  putCachedAudio,
  getSynthesisIdentity,
  syncSynthesisIdentity,
} from './cache';
import { DEFAULT_VOICE_ID, voiceIdForLanguage } from '@/lib/providers/tts/voices';

/**
 * 默认业务音色 ID，与 /api/tts 的默认值（voices.ts 的 DEFAULT_VOICE_ID）保持一致。
 *
 * 注意这里传的是**业务音色 ID**（如 zh-female-warm），不是供应商音色名
 * （如 Gemini 的 Kore）——供应商由服务端 TTS_PROVIDER 决定，客户端不感知，
 * 见 docs/prd/tts-provider-adapter.md「接口协议」。
 */
export const DEFAULT_TTS_VOICE = DEFAULT_VOICE_ID;

export { voiceIdForLanguage };

/** 服务端下发合成身份的响应头，见 lib/providers/tts/cache.ts */
const SYNTHESIS_HEADER = 'X-TTS-Synthesis';

export interface SynthesizeOptions {
  /**
   * 业务音色 ID，见 src/lib/providers/tts/voices.ts。
   *
   * 语言不单独传：服务端从音色定义推导（resolveVoice → definition.languageCode），
   * 音色就是语言的唯一入口。客户端再开一个 languageCode 通道只会出现
   * "中文音色配英文朗读指令"这种自相矛盾的组合。
   */
  voice?: string;
  /** 用量上报归因：这段文字来自哪个会话 / 哪条消息 */
  chatId?: string;
  messageId?: string;
  signal?: AbortSignal;
}

export class TtsRequestError extends Error {
  readonly status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'TtsRequestError';
    this.status = status;
  }

  /** 402：Credits 余额不足，调用方要给出比"朗读失败"更具体的提示 */
  get isInsufficientCredits(): boolean {
    return this.status === 402;
  }

  /** 503：当前配置的供应商未就绪，重试无意义 */
  get isServiceUnavailable(): boolean {
    return this.status === 503;
  }
}

/**
 * 合成单句音频。
 *
 * 说明：这里刻意没有走 SWR。SWR 面向"声明式、可缓存的 JSON 数据"，而朗读是
 * 命令式的音频流水线——播放引擎按播放进度决定何时取哪一句、随时可能中断，
 * 响应是二进制 Blob 且由 Cache Storage 长期缓存（见 ./cache.ts），
 * 用 SWR 反而要绕开它的缓存与重验证语义。
 */
export async function synthesizeSentence(
  text: string,
  { voice = DEFAULT_TTS_VOICE, chatId, messageId, signal }: SynthesizeOptions = {},
): Promise<Blob> {
  const trimmed = text.trim();
  if (!trimmed) {
    throw new TtsRequestError('Empty text');
  }

  // 缓存 key 只按影响音频内容的因素算：chatId / messageId 只是计费归因，
  // 同一句话换个消息 id 音频是一样的，纳入 key 只会白白漏掉缓存。
  const cacheKey = await buildTtsCacheKey({
    text: trimmed,
    voice,
    identity: getSynthesisIdentity(),
  });
  const cached = await getCachedAudio(cacheKey);
  if (cached) {
    return cached;
  }

  const response = await customFetch('/api/tts', {
    method: 'POST',
    body: JSON.stringify({ text: trimmed, voice, chatId, messageId }),
    signal,
  });

  // customFetch 不抛异常：失败时返回 Error 对象，401 时返回 undefined
  if (!(response instanceof Response)) {
    if (response instanceof Error) {
      // 主动中断保持原样抛出，调用方靠 AbortError 区分"取消"与"失败"
      if (response.name === 'AbortError') throw response;
      throw new TtsRequestError(response.message);
    }
    throw new TtsRequestError('Unauthorized', 401);
  }

  if (!response.ok) {
    let message = `TTS request failed: ${response.status}`;
    try {
      const body = await response.json();
      if (body?.message) message = body.message;
    } catch {
      // 非 JSON 响应，保留默认信息
    }
    throw new TtsRequestError(message, response.status);
  }

  const blob = await response.blob();
  if (blob.size === 0) {
    throw new TtsRequestError('Empty audio response');
  }

  // 用响应头校准本地身份：服务端换了版本或供应商就会在这里清空旧缓存。
  // 校准后按新身份重算 key 再写入——否则这次的音频会落在刚被作废的旧 key 上，
  // 下次永远命中不到。重算只是一次哈希，可以忽略不计。
  const identity = await syncSynthesisIdentity(response.headers.get(SYNTHESIS_HEADER));
  const freshKey = await buildTtsCacheKey({ text: trimmed, voice, identity });

  await putCachedAudio(freshKey, blob);
  return blob;
}
